import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

type TextVariant =
  | 'body'
  | 'subtle'
  | 'cardTitle'
  | 'heroValue'
  | 'unit'
  | 'section'
  | 'subSection'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'reportBody';

interface VariantStyle {
  className: ClassValue;
}

const variantStyles: Record<TextVariant, VariantStyle> = {
  body: { className: 'text-base' },
  subtle: { className: 'text-sm text-gray-600' },
  cardTitle: { className: 'text-lg font-medium' },
  section: { className: 'text-xl font-medium' },
  subSection: { className: 'text-md font-medium' },
  heroValue: { className: 'text-4xl font-medium' },
  unit: { className: 'text-xs text-gray-400' },
  reportBody: { className: 'text-md font-light' },
  h1: { className: 'text-4xl font-semibold text-primary-800' },
  h2: { className: 'text-3xl font-semibold' },
  h3: { className: 'text-2xl font-semibold font-heading text-primary-800' },
  h4: { className: 'text-xl font-semibold text-primary-800 font-heading' },
};

interface TextProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TextVariant;
  includeHover?: boolean;
  hoverText?: string;
}

const Text: React.FC<TextProps> = ({
  variant = 'body',
  includeHover = false,
  className,
  children,
  hoverText,
  ...props
}) => {
  const { className: variantStyle } = variantStyles[variant];
  const styles = cn(variantStyle, className);

  if (includeHover) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger className={cn('text-left', styles)} {...props}>
            <p>{children}</p>
          </TooltipTrigger>
          <TooltipContent className="z-50">
            <p className="text-sm">{hoverText || children}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <p className={styles} {...props}>
      {children}
    </p>
  );
};

export default Text;
